import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Filters } from '../../classes/user-preferences';
import { CommunicationService } from '../../services/communication.service';
import { HasSubscriptions } from '../has-subscriptions';
import { HasMinMax } from '../forms/min-max-slider/min-max-slider.component';
import {ListingCategory, SCREEN_SIZE, UpdatedSince} from '../../classes/enums';
import { SelectOption } from '../../components/forms/select/select.component';
import { Helper } from '../../classes/helper';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { PreferencesService } from '../../services/preferences.service';
import { debounceTime, take, skip } from 'rxjs/operators';

export abstract class FiltersComponent extends HasSubscriptions implements AfterViewInit, OnInit, OnChanges {
  filtersForm: FormGroup;

  onlyRentals: boolean = false;
  onlyOpenHouses: boolean = false;
  showAllPropTypes: boolean = true;

  minPriceOptions: SelectOption[] = [];
  maxPriceOptions: SelectOption[] = [];
  // Luxury, Residential or Commercial
  currentShownListingType: ListingCategory;
  filteredPriceValues: number[];

  bedOptions: SelectOption[] = <any[]>[
    { text: 'Any', value: {} },
    { text: '1', value: { min: 1, max: 1 } },
    { text: '1+', value: { min: 1 } },
    { text: '2', value: { min: 2, max: 2 } },
    { text: '2+', value: { min: 2 } },
    { text: '3', value: { min: 3, max: 3 } },
    { text: '3+', value: { min: 3 } },
    { text: '4', value: { min: 4, max: 4 } },
    { text: '4+', value: { min: 4 } }
  ];

  updatedSinceOptions: SelectOption[] = <any[]>[
    { key: 'filters.updatedSince.default', value: UpdatedSince.ANY },
    { key: 'filters.updatedSince.yesterday', value: UpdatedSince.YESTERDAY },
    { key: 'filters.updatedSince.lastWeek', value: UpdatedSince.LASTWEEK },
    { key: 'filters.updatedSince.lastMonth', value: UpdatedSince.LASTMONTH },
  ];

  bathOptions: SelectOption[] = <any[]>[
    { text: 'Any', value: {} },
    { text: '1', value: { min: 1, max: 1 } },
    { text: '1+', value: { min: 1 } },
    { text: '2', value: { min: 2, max: 2 } },
    { text: '2+', value: { min: 2 } },
    { text: '3', value: { min: 3, max: 3 } },
    { text: '3+', value: { min: 3 } },
    { text: '4', value: { min: 4, max: 4 } },
    { text: '4+', value: { min: 4 } }
  ];

  rentalValues = [
    0,
    100, 200, 300, 400, 500, 600, 700, 800, 900, 1000,
    1100, 1200, 1300, 1400, 1500, 1600, 1700, 1800, 1900, 2000,
    2100, 2200, 2300, 2400, 2500, 2600, 2700, 2800, 2900, 3000,
    3200, 3400, 3600, 3800, 4000,
    4200, 4400, 4600, 4800, 5000,
    6000, Number.MAX_SAFE_INTEGER // Show 6000+
  ];

  lowerPriceValues = [
    25000, 50000, 75000
  ];

  priceValues = [
    0, 25000, 50000, 75000, 100000,
    125000, 150000, 175000, 200000,
    225000, 250000, 275000, 300000,
    325000, 350000, 375000, 400000,
    425000, 450000, 475000, 500000,
    550000, 600000,
    650000, 700000,
    750000, 800000,
    850000, 900000,
    950000, 1000000,
    1100000, 1200000, 1300000, 1400000, 1500000,
    1600000, 1700000, 1800000, 1900000, 2000000,
    2500000, 3000000, 4000000, 5000000, 6000000, 7000000, 8000000, 9000000, 10000000, Number.MAX_SAFE_INTEGER //show $10M+
  ];

  sizeValues = [
    0, 500, 550, 600, 650, 700, 750, 800, 850, 900, 950, 1000,
    1250, 1500, 1750, 2000,
    2250, 2500, 2750, 3000,
    3250, 3500, 3750, 4000,
    4250, 4500, 4750, 5000
  ];

  screenSize: SCREEN_SIZE;

  // Enums
  SCREEN_SIZE = SCREEN_SIZE;
  ListingCategory = ListingCategory;

  constructor(
    protected _preferencesService: PreferencesService,
    protected communicationService: CommunicationService,
    protected translateService: TranslateService,
    protected router: Router,
    protected fb: FormBuilder,
    protected cd: ChangeDetectorRef) {
    super();
    this.filtersForm = this.fb.group({
      listingTypes: this.fb.group({
        house: false,
        condo: false,
        townhouse: false,
        recreational: false,
        farm: false,
        land: false,
        mobileHome: false
      }),
      beds: [{}],
      baths: [{}],
      updatedSince: [{}]
    });
  }

  ngOnInit(): void {
    this.addSub(this.filtersForm.get('listingTypes').valueChanges.pipe(debounceTime(10)).subscribe((s) => {
      if (this.onlyRentals && Object.keys(s).some((k) => s[k])) this.toggleRentals(false);
      if (!this.onlyRentals && Object.keys(s).every((k) => !s[k])) this.showAllPropTypes = true;
      else this.showAllPropTypes = false;
    }));

    this.filtersForm.addControl('minPrice', this.fb.control(0));
    this.filtersForm.addControl('maxPrice', this.fb.control(0));
    this.filtersForm.controls['updatedSince'].setValue(UpdatedSince.ANY);

    this.addSub(this.communicationService.screenSize.subscribe((s) => this.screenSize = s));

    // NOTE: should we skip 1 here because the currentShownListingType is a behavior subject
    this.addSub(this.communicationService.listings.currentShownListingType.subscribe((c) => {
      this._resetForm();
      this.currentShownListingType = c;
      this.SetPriceFilters();
    }));
    this._preferencesService.filtersChanged.pipe(take(1)).toPromise().then((filters) => this._updateFiltersForm(filters));
    this.addSub(this.filtersForm.get('minPrice').valueChanges.subscribe((s) => this.updateMaxPriceOptions()));
  }

  SetPriceFilters() {
    this.filteredPriceValues = this.currentShownListingType === ListingCategory.LUXURY ?
      this.priceValues.filter(value => this.lowerPriceValues.indexOf(value) === -1) : this.priceValues;
    this.minPriceOptions = this.filteredPriceValues.map((value) => new SelectOption('$' + Helper.formatPrice(value), value));
    this.maxPriceOptions = this.filteredPriceValues.map((value) => new SelectOption('$' + Helper.formatPrice(value), value));

    // No need for $10M+ in min price filter
    this.minPriceOptions.splice(-1, 1);
    // Add $3M+ in max price filter
    this.maxPriceOptions[this.maxPriceOptions.length - 1].text = this.maxPriceOptions[this.maxPriceOptions.length - 2].text + '+';

    this.minPriceOptions[0].key = 'filters.no-min';
    this.maxPriceOptions[0].key = 'filters.no-max';
  }

  ngAfterViewInit(): void {
    // Skipping this broadcast the first time the component is enabled. We only want to broadcast future changes.
    // It's in afterViewInit to make sure it doesn't get fired while the form is being set up.
    this.addSub(this.filtersForm.valueChanges.pipe(debounceTime(10)).pipe(skip(1)).subscribe((s) => {
      this.broadcastChangedFilters();
    }));

    this.addSub(this.communicationService.filters.resetAndUpdatePropType.subscribe((newPropType) => {
      // HACK: Dont know why I need the timeout here, but apparently it works
      setTimeout(() => {
        this._resetForm();
        (<FormGroup> this.listingTypesFg).controls[newPropType].setValue(true);
        this.broadcastChangedFilters();
      });
    }));
  }

  // TODO: Currently a bug when you switch to rentals, or back, the price/rental price changes but it screws up the values on the slider
  ngOnChanges(changes: SimpleChanges): void {  }

  onReset() {
    this._resetForm();
    this.onlyRentals = false;
    this.onlyOpenHouses = false;
    this.broadcastChangedFilters();
  }

  clearListingTypes(setRentalFalse: boolean) {
    this.listingTypesFg.setValue({
      house: false,
      condo: false,
      townhouse: false,
      recreational: false,
      farm: false,
      land: false,
      mobileHome: false
    });
    if (setRentalFalse) this.onlyRentals = false;
  }

  broadcastChangedFilters() {
    const value = Object.assign(new Filters(), this.filtersForm.value);
    value.rentalsOnly = this.onlyRentals;
    value.hasOpenHouse = this.onlyOpenHouses;
    this._preferencesService.updateFilters(value);
    this.communicationService.searchParametersChanged.next();
  }

  toMagnitudes(parseAsPrice: boolean = false) {
    return parseAsPrice ? Helper.toDollarMagnitudes : Helper.toMagnitudes;
  }

  compareWithHasMinMax(item1: HasMinMax, item2: HasMinMax): boolean {
    return Helper.compareWithHasMinMax(item1, item2);
  }

  get listingTypesFg() {
    return this.filtersForm.get('listingTypes');
  }

  // If rentals are allowed, untoggle all listings and property types
  // if rentals not allowed, dont do anything
  toggleRentals(newCheckedValue) {
    this.filtersForm.patchValue({ price: {} });
    this.onlyRentals = newCheckedValue;
    if (this.onlyRentals) this.clearListingTypes(false);
  }

  /**
   * Whether to only show listings that have an open house
   */
  toggleOpenHouses(newCheckedValue) {
    this.onlyOpenHouses = newCheckedValue;
    this.broadcastChangedFilters();
  }

  // let the child figure out what to update
  protected _updateFiltersForm(filters: Filters) {
    this.onlyRentals = filters.rentalsOnly;
    this.onlyOpenHouses = filters.hasOpenHouse;
    this.showAllPropTypes = !this.onlyRentals;
    this.filtersForm.patchValue({
      listingTypes: {
        house: filters.listingTypes.house,
        condo: filters.listingTypes.condo,
        townhouse: filters.listingTypes.townhouse,
        recreational: filters.listingTypes.recreational,
        farm: filters.listingTypes.farm,
        land: filters.listingTypes.land,
        mobileHome: filters.listingTypes.mobileHome,
      },
      beds: filters.beds || {},
      baths: filters.baths || {},
      updatedSince: !filters.updatedSince ? UpdatedSince.ANY : filters.updatedSince
    });
  }
  protected _resetForm() {
    this.filtersForm.patchValue({
      listingTypes: {
        house: false,
        condo: false,
        townhouse: false,
        recreational: false,
        farm: false,
        land: false,
        mobileHome: false
      },
      beds: {},
      baths: {},
      stories: {},
      businessTypes: {},
      updatedSince: UpdatedSince.ANY
    });
  }


  updateMaxPriceOptions() {
    const minValue = this.filtersForm.controls['minPrice'].value;
    const maxValue = this.filtersForm.controls['maxPrice'].value;
    // Reset the max value in case the min value is greater
    if (minValue > maxValue) {
      this.filtersForm.controls['maxPrice'].setValue(0);
    }
    // Disable the max options if the min value is greater than the option
    this.maxPriceOptions.forEach((option) => {
      option.disabled = minValue > option.value && option.value !== 0;
      return option;
    });
    this.cd.detectChanges();
  }
}

@Component({
  selector: 'app-filters-desktop',
  templateUrl: './filters.desktop.component.html',
  styleUrls: ['./filters.component.scss', './filters.desktop.component.scss']
})
export class FiltersDesktopComponent extends FiltersComponent implements AfterViewInit, OnInit {
  showPropertyTypes: boolean = false;
  showPrice: boolean = false;
  showBedrooms: boolean = false;
  showMore: boolean = false;
  showSize: boolean = false;

  minRentalPriceOptions: SelectOption[] = [];
  maxRentalPriceOptions: SelectOption[] = [];

  minSizeOptions: SelectOption[] = [];
  maxSizeOptions: SelectOption[] = [];

  @ViewChild('form') formElementRef: ElementRef;

  ngOnInit() {
    super.ngOnInit();

    this.filtersForm.addControl('minSize', this.fb.control(0));
    this.filtersForm.addControl('maxSize', this.fb.control(0));
    this.filtersForm.controls['updatedSince'].setValue(UpdatedSince.ANY);

    this.minRentalPriceOptions = this.rentalValues.map((value) => new SelectOption('$' + Helper.formatPrice(value, 10000), value));
    this.maxRentalPriceOptions = this.rentalValues.map((value) => new SelectOption('$' + Helper.formatPrice(value, 10000), value));

    // No need for $6000+ in min rental price filter
    this.minRentalPriceOptions.splice(-1, 1);
    // Add $6000+ in max rental price filter
    this.maxRentalPriceOptions[this.maxRentalPriceOptions.length - 1].text = this.maxRentalPriceOptions[this.maxRentalPriceOptions.length - 2].text + '+';

    this.minSizeOptions = this.sizeValues.map((value) => new SelectOption(value, value));
    this.maxSizeOptions = this.sizeValues.map((value) => new SelectOption(value, value));

    // TODO: Translations
    this.minRentalPriceOptions[0].text = 'No Min';
    this.maxRentalPriceOptions[0].text = 'No Max';
    this.minSizeOptions[0].text = 'No Min';
    this.maxSizeOptions[0].text = 'No Max';

    this.addSub(this.filtersForm.get('minSize').valueChanges.subscribe((s) => this.updateMaxSizeOptions()));

    // TODO: Translations
    this.bathOptions.forEach((option) => option.text = option.text + ' Baths');
    this.bedOptions.forEach((option) => option.text = option.text + ' Beds');
  }

  @HostListener('window:click', ['$event'])
  onPageClick($event: MouseEvent) {
    // Hide all the dropdowns if the dropdowns are open and they click something not a dropdown
    if (!this.formElementRef.nativeElement.contains($event.target)) this.closeDropdowns();
  }

  updateMaxSizeOptions() {
    const minValue = this.filtersForm.get('minSize').value;
    // Reset the max value in case the min value is greater
    if (minValue > this.filtersForm.get('maxSize').value) this.filtersForm.get('maxSize').setValue(0);
    // Disable the max options if the min value is greater than the option
    this.maxSizeOptions = this.maxSizeOptions.map((option) => {
      if (minValue > option.value && option.value !== 0) option.disabled = true;
      else option.disabled = false;
      return option;
    });
    this.cd.detectChanges();
  }

  broadcastChangedFilters() {
    const value = Object.assign(new Filters(), this.filtersForm.value);
    value.price = {
      min: this.filtersForm.get('minPrice').value,
      max: this.filtersForm.get('maxPrice').value
    };
    value.size = {
      min: this.filtersForm.get('minSize').value,
      max: this.filtersForm.get('maxSize').value
    };
    value.rentalsOnly = this.onlyRentals;
    value.hasOpenHouse = this.onlyOpenHouses;
    this._preferencesService.updateFilters(value);
    this.communicationService.searchParametersChanged.next();
  }

  onSubmit() {
    this.closeDropdowns();
  }

  checkFocus(event: FocusEvent, parentElement, elementToFocus) {
    if (!parentElement.contains(event.relatedTarget)) {
      this.closeDropdowns(null, null, elementToFocus);
    }
  }

  /**
   * Close all dropdowns, and set the status of one dropdown to the desired status
   * @param dropdownName dropdownName we want to specifically toggle
   * @param newStatus the new status
   */
  closeDropdowns(dropdownName?: string, newStatus?: boolean, elementToFocus?) {
    this.showPropertyTypes = false;
    this.showPrice = false;
    this.showBedrooms = false;
    this.showMore = false;
    this.showSize = false;

    if (dropdownName) this[dropdownName] = newStatus;
    if (!!elementToFocus) elementToFocus.focus();
  }

  onReset() {
    super.onReset();
    this.closeDropdowns();
  }

  toggleRentals(newCheckedValue) {
    super.toggleRentals(newCheckedValue);
    this.filtersForm.patchValue({
      minPrice: 0,
      maxPrice: 0
    });
  }

  protected _updateFiltersForm(filters: Filters) {
    super._updateFiltersForm(filters);
    this.filtersForm.patchValue({
      minPrice: filters.price && filters.price.min ? filters.price.min : 0,
      maxPrice: filters.price && filters.price.max ? filters.price.max : 0,
      minSize: filters.size && filters.size.min ? filters.size.min : 0,
      maxSize: filters.size && filters.size.max ? filters.size.max : 0
    });
  }

  protected _resetForm() {
    super._resetForm();
    this.filtersForm.patchValue({
      minSize: 0,
      maxSize: 0,
      minPrice: 0,
      maxPrice: 0
    });
  }

  get priceText(): string {
    const minPrice = this.filtersForm.get('minPrice').value;
    const maxPrice = this.filtersForm.get('maxPrice').value;
    // TODO: Translations
    if (minPrice === 0 && maxPrice === 0) return 'Any Price';
    else if ((minPrice > 0 && maxPrice === 0) || (maxPrice === Number.MAX_SAFE_INTEGER)) return Helper.toDollarMagnitudes(minPrice) + '+';
    else if (minPrice === 0 && maxPrice > 0) return Helper.toDollarMagnitudes(0) + ' - ' + Helper.toDollarMagnitudes(maxPrice);
    else return Helper.toDollarMagnitudes(minPrice) + ' - ' + Helper.toDollarMagnitudes(maxPrice);
  }

  get sizeText(): string {
    const minSize = this.filtersForm.get('minSize').value;
    const maxSize = this.filtersForm.get('maxSize').value;
    // TODO: translations
    if (!minSize && !maxSize) return 'Any Square Feet';
    else if (minSize && !maxSize) return Helper.toMagnitudes(minSize) + '+ sqft';
    else if (!minSize && maxSize) return Helper.toMagnitudes(0) + ' - ' + Helper.toMagnitudes(maxSize) + '+ sqft';
    else return Helper.toMagnitudes(minSize) + ' - ' + Helper.toMagnitudes(maxSize) + ' sqft';
  }

  get propertyText(): string {
    const totalPropsEnabled = Object.keys(this.listingTypesFg.value).filter((key) => this.listingTypesFg.value[key]).length;
    // TODO: Translations
    if (this.onlyRentals) return 'All Rentals';
    return totalPropsEnabled > 0 ? `Property Types (${totalPropsEnabled})` : 'All Property Types';
  }

  get moreText(): string {
    let totalExtraValuesEnabled = 0;
    if (this.filtersForm.get('minSize').value || this.filtersForm.get('maxSize').value) totalExtraValuesEnabled++;
    if (this.filtersForm.get('baths').value && (this.filtersForm.get('baths').value.min || this.filtersForm.get('baths').value.max)) totalExtraValuesEnabled++;
    if (this.filtersForm.get('beds').value && (this.filtersForm.get('beds').value.min || this.filtersForm.get('beds').value.max)) totalExtraValuesEnabled++;
    if (this.filtersForm.get('updatedSince').value && this.filtersForm.get('updatedSince').value !== UpdatedSince.ANY) totalExtraValuesEnabled++;

    return 'More' + (totalExtraValuesEnabled > 0 ? ` (${totalExtraValuesEnabled})` : '');
  }

}

@Component({
  selector: 'app-filters-mobile',
  templateUrl: './filters.mobile.component.html',
  styleUrls: ['./filters.component.scss', './filters.mobile.component.scss']
})
export class FiltersMobileComponent extends FiltersComponent implements AfterViewInit, OnInit {
  @Output() closed = new EventEmitter<void>();

  showingAllTypes: boolean;
  listingsAvailable: number;

  ngOnInit() {
    super.ngOnInit();

    this.filtersForm.addControl('size', this.fb.control({}));
    this.filtersForm.addControl('price', this.fb.control({}));
    this.addSub(this.communicationService.searchResultsAvailable.subscribe(results => {
      this.listingsAvailable = results;
      const drawerTitleRightText = this.translateService.instant('filters.n-results', { n: results });
      document.querySelector('app-drawer .drawer-sub-title-right').innerHTML = drawerTitleRightText;
    }));
  }

  onSubmit() {
    this.closed.emit();
    // TODO: Changing the location doesnt do anything here
  }

  protected _updateFiltersForm(filters: Filters) {
    super._updateFiltersForm(filters);
    this.filtersForm.patchValue({
      size: filters.size,
      price: filters.price
    });
  }
  protected _resetForm() {
    super._resetForm();
    this.filtersForm.patchValue({
      size: {},
      price: {}
    });
  }
}
